/**
 * SiteVisitEmailModal — R4
 *
 * Preview & Send modal for site visit PDF reports.
 * Feature-gated by SITE_VISIT_EMAIL_ENABLED.
 *
 * Sections:
 *  - Inline PDF preview (reuses the existing report/download blob)
 *  - Recipient list pre-populated from portal members + scope contacts
 *  - Free-form add recipient input (auto-added to project contacts on send)
 *  - Send button (transitions to submitted)
 *  - "Submit without sending" secondary action
 */
import { useState, useEffect, useRef } from 'react';
import { Mail, X, Plus, Send, Loader2, FileText, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  useSiteVisitEmailRecipients,
  useSendSiteVisitEmail,
  useSubmitSiteVisitWithoutSending,
  type SiteVisitEmailRecipient,
} from '@/hooks/useSiteVisits';
import { api } from '@/lib/api';
import { isValidEmail } from '@/lib/email-validation';
import { toast } from 'sonner';

interface Props {
  open: boolean;
  onClose: () => void;
  visitId: string;
  visitDate: string;
  projectName?: string;
}

export function SiteVisitEmailModal({ open, onClose, visitId, visitDate, projectName }: Props) {
  const { data: recipientsData, isLoading: recipientsLoading } = useSiteVisitEmailRecipients(
    open ? visitId : null
  );

  const sendEmail = useSendSiteVisitEmail();
  const submitWithout = useSubmitSiteVisitWithoutSending();

  // Recipient state — seeded from API on open
  const [recipients, setRecipients] = useState<SiteVisitEmailRecipient[]>([]);
  const [addInput, setAddInput] = useState('');
  const [addError, setAddError] = useState('');
  const [customNote, setCustomNote] = useState('');

  // PDF preview state
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const blobRef = useRef<string | null>(null);

  // Seed recipients once data arrives
  useEffect(() => {
    if (recipientsData?.recipients) {
      setRecipients(recipientsData.recipients);
    }
  }, [recipientsData]);

  // Load PDF preview when modal opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const load = async () => {
      setPdfLoading(true);
      setPdfError('');
      try {
        const url = await api.siteVisits.previewReportBlob(visitId);
        if (!cancelled) {
          blobRef.current = url;
          setPdfBlobUrl(url);
        }
      } catch {
        if (!cancelled) setPdfError('Could not load PDF preview.');
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [open, visitId]);

  // Revoke blob on close
  useEffect(() => {
    if (!open && blobRef.current) {
      URL.revokeObjectURL(blobRef.current);
      blobRef.current = null;
      setPdfBlobUrl(null);
    }
  }, [open]);

  const handleRemoveRecipient = (email: string) => {
    setRecipients((prev) => prev.filter((r) => r.email !== email));
  };

  const handleAddRecipient = () => {
    const email = addInput.trim().toLowerCase();
    if (!isValidEmail(email)) {
      setAddError('Enter a valid email address.');
      return;
    }
    if (recipients.some((r) => r.email === email)) {
      setAddError('This email is already in the list.');
      return;
    }
    if (recipients.length >= 20) {
      setAddError('Maximum 20 recipients per send.');
      return;
    }
    setAddError('');
    setRecipients((prev) => [...prev, { email, name: email, source: 'scope_contact' }]);
    setAddInput('');
  };

  const handleSend = async () => {
    if (recipients.length === 0) {
      toast.error('Add at least one recipient.');
      return;
    }
    try {
      await sendEmail.mutateAsync({
        visitId,
        toRecipients: recipients.map((r) => r.email),
        customNote: customNote.trim() || undefined,
      });
      onClose();
    } catch {
      // error toast handled by mutation's onError
    }
  };

  const handleSubmitWithout = async () => {
    try {
      await submitWithout.mutateAsync(visitId);
      onClose();
    } catch {
      // error toast handled by mutation's onError
    }
  };

  const isBusy = sendEmail.isPending || submitWithout.isPending;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        data-testid="site-visit-email-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-muted-foreground" />
            Preview &amp; Send Site Visit Report
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">
            {projectName} — {visitDate}
          </p>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {/* PDF Preview */}
          <div>
            <Label className="mb-1.5 block text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Report Preview
            </Label>
            <div className="border rounded-md overflow-hidden bg-muted/30" style={{ height: 380 }}>
              {pdfLoading && (
                <div className="flex items-center justify-center h-full gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading PDF…
                </div>
              )}
              {pdfError && !pdfLoading && (
                <div className="flex items-center justify-center h-full gap-2 text-sm text-destructive">
                  <AlertCircle className="w-4 h-4" />
                  {pdfError}
                </div>
              )}
              {pdfBlobUrl && !pdfLoading && (
                <iframe
                  src={pdfBlobUrl}
                  title="Site visit report preview"
                  className="w-full h-full border-0"
                  sandbox="allow-same-origin"
                  data-testid="pdf-preview-iframe"
                />
              )}
            </div>
          </div>

          {/* Recipients */}
          <div>
            <Label className="mb-1.5 block text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Recipients
            </Label>

            {recipientsLoading && (
              <p className="text-xs text-muted-foreground">Loading contacts…</p>
            )}

            {recipients.length > 0 && (
              <div
                className="flex flex-wrap gap-1.5 mb-2"
                data-testid="recipient-list"
              >
                {recipients.map((r) => (
                  <span
                    key={r.email}
                    className="inline-flex items-center gap-1 text-sm bg-secondary text-secondary-foreground rounded-full px-3 py-1"
                  >
                    <span>{r.email}</span>
                    <button
                      aria-label={`Remove ${r.email}`}
                      className="hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-full"
                      onClick={() => handleRemoveRecipient(r.email)}
                      disabled={isBusy}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {recipients.length === 0 && !recipientsLoading && (
              <p className="text-xs text-muted-foreground mb-2">
                No pre-populated recipients. Add emails below.
              </p>
            )}

            {/* Add recipient */}
            <div className="flex gap-2">
              <Input
                placeholder="Add email address…"
                value={addInput}
                onChange={(e) => {
                  setAddInput(e.target.value);
                  setAddError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddRecipient();
                  }
                }}
                disabled={isBusy}
                className="flex-1"
                data-testid="add-recipient-input"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddRecipient}
                disabled={isBusy || !addInput.trim()}
                data-testid="add-recipient-button"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {addError && (
              <p className="text-xs text-destructive mt-1">{addError}</p>
            )}
          </div>

          {/* Optional note */}
          <div>
            <Label htmlFor="custom-note" className="mb-1.5 block text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Personal Note (optional)
            </Label>
            <Textarea
              id="custom-note"
              value={customNote}
              onChange={(e) => setCustomNote(e.target.value)}
              placeholder="Add a message to include with the report…"
              rows={2}
              disabled={isBusy}
              className="text-sm"
              data-testid="custom-note-input"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between gap-3 pt-1 border-t">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleSubmitWithout}
              disabled={isBusy}
              className="text-muted-foreground"
              data-testid="submit-without-sending-button"
            >
              {submitWithout.isPending ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <FileText className="w-4 h-4 mr-1" />
              )}
              Submit without sending
            </Button>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onClose}
                disabled={isBusy}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleSend}
                disabled={isBusy || recipients.length === 0}
                data-testid="send-email-button"
              >
                {sendEmail.isPending ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-1" />
                )}
                Send Report
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
